import { usePageInfo } from '@/pages/opinion/hooks/common';
import useStorageOperation from '@/pages/opinion/hooks/useStorageOperation';
import { LanguageType } from '@/pages/opinion/types/common';
import { urlToMap } from '@op/utils/url';
import { useMemo, useRef, useState } from 'react';
import { SortBy } from 'tea-component/lib/table/addons';
import { useLocation } from 'umi';

const defaultValue = {
  pageInfo: { page: 1, page_size: 50 },
  sortBy: {
    by: 'comment_time',
    order: 'desc',
  },
  reviewType: 'all',
  matchModel: 'fuzzy',
  search: '',
  selectKeyword: [],
  defaultKeyword: [],
  isExcludeInvalid: false,
  selectRenderKeyword: { origin: [], zh: [], en: [] },
};

type RenderKeywordType = {
  origin: string[];
  zh: string[];
  en: string[];
};

type ExportKeys =
  | 'setSearch'
  | 'changePageInfo'
  | 'setSortBy'
  | 'setMatchModel'
  | 'setSelectKeyword'
  | 'setSelectRenderKeyword'
  | 'changeDefaultKeyword'
  | 'setIsExcludeInvalid'
  | 'setShowSentiment';

type ExportFuncType = { [key in ExportKeys]: (value?: any) => void };

export default function useFeedsData({ storageKey }: { storageKey: string }) {
  const { data, setStore } = useStorageOperation({
    storage: sessionStorage,
    storageKey,
    model: 'merge',
    defaultValue,
  });

  const defaultFilterValue = Object.assign({}, defaultValue, data);
  const [isExcludeInvalid, setIsExcludeInvalid] = useState(
    defaultFilterValue.isExcludeInvalid,
  );
  const { pageInfo, changePageInfo } = usePageInfo({
    defaultPageInfo: defaultFilterValue.pageInfo,
  });

  const location = useLocation();
  const searchParams = urlToMap(location.search);
  const by = searchParams.get('by');
  const order = searchParams.get('order');

  if (by && order) {
    defaultFilterValue.sortBy = {
      by,
      order,
    };
  }
  const [channelList, setChannelList] = useState([]);
  const [sortBy, setSortBy] = useState<SortBy<any>>(defaultFilterValue.sortBy);
  const [matchModel, setMatchModel] = useState(defaultFilterValue.matchModel);
  const [search, setSearch] = useState(defaultFilterValue.search);
  const [selectKeyword, setSelectKeyword] = useState<Array<string>>(
    defaultFilterValue.selectKeyword,
  );
  const defaultKeyword = useRef<Array<string>>(
    defaultFilterValue.defaultKeyword,
  );
  const [showSentiment, setShowSentiment] = useState(
    defaultFilterValue.showSentiment,
  );

  const [languages, setLanguages] = useState<LanguageType[]>(
    defaultFilterValue.showSentiment ? ['origin', 'en'] : ['origin'],
  );
  const [filterBoxValue, setFilterBoxValue] = useState({});

  const [selectRenderKeyword, setSelectRenderKeyword] =
    useState<RenderKeywordType>(defaultFilterValue.selectRenderKeyword);

  const hasSearch = useMemo(() => search.trim().length > 0, [search]);

  // 模糊搜索：使用 feeds/comments 接口，不隐藏 keyword 板块
  const isFuzzySearch = useMemo(
    () => hasSearch && matchModel === 'fuzzy',
    [hasSearch, matchModel],
  );
  // 精准/拓展搜索：使用 feeds/search 接口，隐藏 keyword 板块
  const isExactOrExtendSearch = useMemo(
    () => hasSearch && matchModel !== 'fuzzy',
    [hasSearch, matchModel],
  );

  // 批量创建修改值得同时还会设置缓存的方法
  const aopFuncMap: ExportFuncType = useMemo(() => {
    return [
      { exportKey: 'changePageInfo', key: 'pageInfo', setFunc: changePageInfo },
      { exportKey: 'setSortBy', key: 'sortBy', setFunc: setSortBy },
      { exportKey: 'setMatchModel', key: 'matchModel', setFunc: setMatchModel },
      { exportKey: 'setSearch', key: 'search', setFunc: setSearch },
      {
        exportKey: 'setShowSentiment',
        key: 'showSentiment',
        setFunc: setShowSentiment,
      },
      {
        exportKey: 'setSelectKeyword',
        key: 'selectKeyword',
        setFunc: setSelectKeyword,
      },
      {
        exportKey: 'setSelectRenderKeyword',
        key: 'selectRenderKeyword',
        setFunc: setSelectRenderKeyword,
      },
      {
        exportKey: 'changeDefaultKeyword',
        key: 'defaultKeyword',
        setFunc: (value: Array<string>) => {
          defaultKeyword.current = value;
        },
      },
      {
        exportKey: 'setIsExcludeInvalid',
        key: 'isExcludeInvalid',
        setFunc: setIsExcludeInvalid,
      },
    ].reduce((pre, cur) => {
      const { exportKey, key, setFunc } = cur;
      return {
        ...pre,
        [exportKey]: (value: any) => {
          setStore({ [key]: value });
          setFunc(value);
        },
      };
    }, {} as ExportFuncType);
  }, []);

  const resetPartFeedsData = (keepKeywords = false) => {
    if (!keepKeywords) {
      setSelectRenderKeyword({
        origin: [],
        zh: [],
        en: [],
      });
      defaultKeyword.current = [];
      setSelectKeyword([]);
    }

    changePageInfo({ page: 1, page_size: 50 });

    const storeData: Record<string, any> = {
      pageInfo: { page: 1, page_size: 50 },
    };
    if (!keepKeywords) {
      storeData.selectKeyword = [];
      storeData.defaultKeyword = [];
      storeData.selectRenderKeyword = { origin: [], zh: [], en: [] };
    }
    setStore(storeData);
  };

  // 下面的所有方法，都得使用aopFuncMap的方法，需要修改记录缓存
  const filterBoxChange = (value: any, key: any) => {
    const { setMatchModel, changePageInfo, setSortBy, setShowSentiment } =
      aopFuncMap;
    setMatchModel(value.match_model);
    setShowSentiment(value.showSentiment);
    if (
      value.showSentiment &&
      !languages?.includes('en') &&
      key === 'showSentiment'
    ) {
      setLanguages([...languages, 'en']);
    }
    setFilterBoxValue(value);
    // 这几个改变不去改变pageInfo数据，但是match_model 并且 是搜索状态就需要修改pageInfo
    if (
      ![undefined, 'download', 'match_model', 'showSentiment'].includes(key) ||
      (key === 'match_model' && hasSearch)
    ) {
      changePageInfo();
    }

    if (key?.includes('sortBy')) {
      setSortBy({
        order: 'desc',
        by: value[key],
      });
    }
  };

  const filterBoxSearch = (value: any) => {
    const { setSortBy, setSearch } = aopFuncMap;
    const hasValue = value.trim().length > 0; // 新的搜索值
    // hasSearch 就是老值是否存在
    if (hasSearch && !hasValue) {
      // 如果之前有，现在没有，前往无搜索状态
      setSortBy({
        by: 'comment_time',
        order: 'desc',
      });
    }

    if (!hasSearch && hasValue) {
      // 如果之前没有，现在有了：前往搜索状态
      setSortBy({
        by: 'relevance',
        order: 'desc',
      });
    }

    // 模糊搜索模式下保留关键词，仅重置分页
    resetPartFeedsData(matchModel === 'fuzzy');
    setSearch(value);
  };

  const filterBoxClear = () => {
    const { changePageInfo, setSortBy, setSearch } = aopFuncMap;
    setSortBy({
      by: 'comment_time',
      order: 'desc',
    });
    changePageInfo();
    setSearch('');
  };

  const keywordChange = ({
    children,
    keywordRender,
    parent,
  }: {
    parent: string[];
    children: string[];
    keywordRender: {
      keyword: string[];
      keyword_cn: string[];
      keyword_en: string[];
    };
  }) => {
    const {
      changeDefaultKeyword,
      setSelectKeyword,
      changePageInfo,
      setSelectRenderKeyword,
    } = aopFuncMap;
    changeDefaultKeyword(parent);
    setSelectKeyword(children);
    changePageInfo();
    setSelectRenderKeyword({
      origin: keywordRender?.keyword,
      zh: keywordRender?.keyword_cn,
      en: keywordRender?.keyword_en,
    });
  };

  return {
    languages,
    setLanguages,
    pageInfo,
    sortBy,
    matchModel,
    search,
    selectKeyword,
    showSentiment,
    defaultKeyword: defaultKeyword.current,
    selectRenderKeyword,
    hasSearch,
    isFuzzySearch,
    isExactOrExtendSearch,
    isExcludeInvalid,
    resetPartFeedsData,
    filterBoxValue,
    filterBoxChange,
    filterBoxSearch,
    filterBoxClear,
    keywordChange,
    channelList, setChannelList,
    ...aopFuncMap,
  };
}
